import React, { useRef } from "react";

import { type IconProp } from "@fortawesome/fontawesome-svg-core";
import {
  faMagnifyingGlass,
  faSparkles,
} from "@fortawesome/pro-regular-svg-icons";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { twJoin } from "tailwind-merge";

import { Card } from "src/base-components/Card";
import { CopyTextIcon } from "src/base-components/CopyTextIcon";
import { Icon } from "src/base-components/Icon";
import { Switch } from "src/base-components/Switch";
import { TextEditor } from "src/base-components/TextEditor/TextEditor";
import { Toolbar } from "src/base-components/TextEditor/Toolbar";
import { FormStepWrapper } from "src/cases/templates/FormStepWrapper";
import { getStepById } from "src/cases/templates/formSteps";
import { CaseTemplateFormValues } from "src/cases/templates/formUtils/types";
import { CaseTemplateVersionCopilotSettingsOperatingModeEnum } from "src/clients/case-management";
import { Accordion } from "src/design-system/Accordion";
import { EmptyState } from "src/design-system/EmptyState";

type CapabilityCardProps = {
  icon: IconProp;
  title: string;
  description: string;
  rightSlot?: React.ReactNode;
  color?: string;
};

const CapabilityCard: React.FC<CapabilityCardProps> = ({
  icon,
  title,
  description,
  rightSlot,
  color = "bg-green-electric",
}) => (
  <div className="flex items-center justify-between gap-x-2 rounded-lg border border-gray-200 bg-white p-2 pr-3">
    <div
      className={twJoin(
        "flex h-10 w-10 shrink-0 items-center justify-center rounded-lg",
        color,
      )}
    >
      <Icon color="text-gray-800" icon={icon} size="md" />
    </div>
    <div className="flex flex-1 flex-col">
      <span className="font-inter-semibold-13px text-gray-800">{title}</span>
      <span className="font-inter-normal-12px text-gray-500">
        {description}
      </span>
    </div>
    <div className="self-start">{rightSlot}</div>
  </div>
);

export const CopilotConfigurationStep: React.FC = () => {
  const step = getStepById("configure-copilot");
  const { setValue, getValues } = useFormContext<CaseTemplateFormValues>();
  const toolbarRef = useRef<HTMLUListElement>(null);

  const copilotEnabled = useWatch<CaseTemplateFormValues>({
    name: "attributes.copilot_settings.copilot_enabled",
  });

  return (
    <FormStepWrapper
      actions={
        <Controller
          name="attributes.copilot_settings.copilot_enabled"
          render={({ field }) => (
            <Switch
              dataLoc="copilot-enable-toggle"
              enabled={field.value as boolean}
              onChange={field.onChange}
            />
          )}
        />
      }
      description={step.description}
      docLink={step.docLink}
      title={step.title}
    >
      {copilotEnabled ? (
        <Accordion defaultValue={["custom-instructions", "capabilities"]}>
          <Accordion.Item
            dataLoc="copilot-custom-instructions"
            description="Set behavioral guidelines and upload reference files. Copilot will use this knowledge to drive its logic and provide specific details during cases."
            title="Custom instructions"
            value="custom-instructions"
          >
            <Card>
              <Card.Content>
                <div className="flex flex-col gap-y-5">
                  <div className="flex flex-col gap-y-2">
                    <div className="flex items-center justify-between">
                      <span className="font-inter-semibold-13px text-gray-800">
                        Prompt
                      </span>
                      <CopyTextIcon
                        dataLoc="copilot-prompt-copy"
                        tooltip="Copy prompt"
                        value={() =>
                          (getValues(
                            "attributes.copilot_settings.copilot_prompt",
                          ) as string) ?? ""
                        }
                        preventDefault
                      />
                    </div>
                    <div
                      className={twJoin(
                        "flex min-w-0 flex-1 flex-col rounded border border-gray-200 bg-white p-3",
                        "group focus-within:border-indigo-600 focus-within:ring-2 focus-within:ring-indigo-500/25",
                      )}
                    >
                      <div className="h-[350px]">
                        <Controller
                          name="attributes.copilot_settings.copilot_prompt"
                          render={({ field }) => (
                            <TextEditor
                              dataLoc="copilot-instructions-prompt"
                              maxHeight="max-h-[350px]"
                              placeholder="Enter custom instructions…"
                              toolbarRef={toolbarRef}
                              value={field.value as string}
                              fullHeight
                              keepEmptyLines
                              onChange={field.onChange}
                            />
                          )}
                        />
                      </div>
                      <div className="flex justify-between gap-x-3 pt-3">
                        <Toolbar
                          ref={toolbarRef}
                          exclude={["heading"]}
                          faded={false}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </Card.Content>
            </Card>
          </Accordion.Item>

          <Accordion.Item
            dataLoc="copilot-capabilities"
            description="Enable the specific tools, specialized agents, and external integrations your Copilot is authorized to access."
            title="Capabilities"
            value="capabilities"
          >
            <Card>
              <Card.Content>
                <CapabilityCard
                  description="Search the web for relevant information during case review."
                  icon={faMagnifyingGlass}
                  rightSlot={
                    <Controller
                      name="attributes.copilot_settings.websearch_tool_enabled"
                      render={({ field }) => (
                        <Switch
                          dataLoc="copilot-web-search-toggle"
                          enabled={field.value as boolean}
                          onChange={field.onChange}
                        />
                      )}
                    />
                  }
                  title="Web search"
                />
              </Card.Content>
            </Card>
          </Accordion.Item>

          <Accordion.Item
            dataLoc="copilot-autonomous-investigation"
            description={
              <span>
                When enabled, Copilot proactively starts the review,
                recommending next steps and pre-filling task and action forms.
                All final actions and submissions{" "}
                <span className="font-inter-semibold-12px">
                  require reviewer&apos;s manual approval.
                </span>
              </span>
            }
            rightSlot={
              <Controller
                name="attributes.copilot_settings.operating_mode"
                render={({ field }) => (
                  <Switch
                    dataLoc="copilot-autonomous-investigation-toggle"
                    enabled={
                      field.value ===
                      CaseTemplateVersionCopilotSettingsOperatingModeEnum.EAGER
                    }
                    onChange={(checked) =>
                      field.onChange(
                        checked
                          ? CaseTemplateVersionCopilotSettingsOperatingModeEnum.EAGER
                          : CaseTemplateVersionCopilotSettingsOperatingModeEnum.LAZY,
                      )
                    }
                  />
                )}
              />
            }
            title="Autonomous case investigation"
            value="autonomous-investigation"
          />
        </Accordion>
      ) : (
        <EmptyState
          actionButton={{
            text: "Enable Copilot",
            onClick: () =>
              setValue("attributes.copilot_settings.copilot_enabled", true),
          }}
          dataLoc="copilot-disabled-empty-state"
          description="Copilot must be enabled to complete configuration for this template."
          headline="Copilot is not enabled"
          icon={faSparkles}
        />
      )}
    </FormStepWrapper>
  );
};
